import { IconPicker } from '@/components/passport/IconPicker';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { ApiError, apiFetch } from '@/lib/api';
import {
  createResource,
  deleteResource,
  fetchActivityDetail,
  updateActivity,
  uploadActivityPhoto,
} from '@/lib/passport-admin';
import { useAuthStore } from '@/stores/auth-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  type PassportActivityType,
  type PassportActivityUpdateInput,
  type PassportIcon,
  type PassportResourceDto,
} from '@tunodo/ticketing-shared';
import { Loader2, Mail, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Zod schema for send-qr-email response
// ---------------------------------------------------------------------------
import { z } from 'zod';
const SendQrEmailResponse = z.object({ sentTo: z.string() });

export const Route = createFileRoute('/_app/admin/passport/activities/$activityId')({
  beforeLoad: () => {
    const { user } = useAuthStore.getState();
    if (user?.role !== 'ORGANIZER') {
      toast.info('Solo los organizadores pueden acceder al panel de pasaporte.');
      throw redirect({ to: '/' });
    }
  },
  component: EditActivityPage,
});

// ---------------------------------------------------------------------------
// Speaker photo upload
// ---------------------------------------------------------------------------
function SpeakerPhotoUpload({
  activityId,
  currentPhotoUrl,
  onUploaded,
}: {
  activityId: string;
  currentPhotoUrl: string | null;
  onUploaded: () => void;
}) {
  const [preview, setPreview] = useState<string | null>(currentPhotoUrl);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFile(file: File) {
    setUploading(true);
    try {
      const data = await uploadActivityPhoto(activityId, file);
      setPreview(data.photoUrl);
      onUploaded();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo subir la imagen.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex items-center gap-3">
      {preview && (
        <img src={preview} alt="preview" className="h-16 w-16 rounded-full object-cover border" />
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleFile(f);
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
      >
        {uploading ? 'Subiendo...' : preview ? 'Cambiar foto' : 'Subir foto'}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Resources list
// ---------------------------------------------------------------------------
function ResourcesList({
  activityId,
  resources,
  eventId,
}: {
  activityId: string;
  resources: PassportResourceDto[];
  eventId: string;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [adding, setAdding] = useState(false);

  async function addResource() {
    if (!title.trim() || !url.trim()) return;
    setAdding(true);
    try {
      const trimmedDescription = description.trim();
      await createResource(activityId, {
        title: title.trim(),
        url: url.trim(),
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
      });
      setTitle('');
      setDescription('');
      setUrl('');
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo agregar el recurso.');
    } finally {
      setAdding(false);
    }
  }

  async function removeResource(resourceId: string) {
    try {
      await deleteResource(resourceId);
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo eliminar el recurso.');
    }
  }

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['passport-admin', 'event', eventId] });
    void queryClient.invalidateQueries({ queryKey: ['passport-admin', 'activity', activityId] });
  }

  return (
    <div className="space-y-2">
      <Label>Recursos</Label>
      {resources.map((r) => (
        <div
          key={r.id}
          className="flex items-start justify-between gap-2 rounded border p-2 text-sm"
        >
          <div className="min-w-0">
            <p className="font-medium truncate">{r.title}</p>
            {r.description && <p className="text-muted-foreground truncate">{r.description}</p>}
            <a
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 truncate block"
            >
              {r.url}
            </a>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={() => void removeResource(r.id)}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      ))}
      <div className="space-y-1 rounded border p-2">
        <Input
          placeholder="Título del recurso *"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Input
          placeholder="Descripción (opcional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <Input
          placeholder="URL (https://...) *"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <Button
          type="button"
          size="sm"
          disabled={adding || !title.trim() || !url.trim()}
          onClick={() => void addResource()}
        >
          {adding ? 'Agregando...' : 'Agregar recurso'}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
function EditActivityPage() {
  const { activityId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: activity,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['passport-admin', 'activity', activityId],
    queryFn: () => fetchActivityDetail(activityId),
  });

  const [type, setType] = useState<PassportActivityType | undefined>(undefined);
  const [title, setTitle] = useState<string | undefined>(undefined);
  const [speakerName, setSpeakerName] = useState<string | undefined>(undefined);
  const [speakerEmail, setSpeakerEmail] = useState<string | undefined>(undefined);
  const [speakerBio, setSpeakerBio] = useState<string | undefined>(undefined);
  const [description, setDescription] = useState<string | undefined>(undefined);
  const [icon, setIcon] = useState<PassportIcon | null | undefined>(undefined);
  const [order, setOrder] = useState<string | undefined>(undefined);
  const [sendQrConfirmOpen, setSendQrConfirmOpen] = useState(false);

  const effectiveType = type ?? activity?.type ?? 'TALK';
  const effectiveTitle = title ?? activity?.title ?? '';
  const effectiveSpeakerName = speakerName ?? activity?.speakerName ?? '';
  const effectiveSpeakerEmail = speakerEmail ?? activity?.speakerEmail ?? '';
  const effectiveSpeakerBio = speakerBio ?? activity?.speakerBio ?? '';
  const effectiveDescription = description ?? activity?.description ?? '';
  const effectiveIcon = icon !== undefined ? icon : (activity?.icon ?? null);
  const effectiveOrder = order ?? (activity ? String(activity.order) : '');

  // isDirty: any local state change means unsaved changes
  const isDirty =
    type !== undefined ||
    title !== undefined ||
    speakerName !== undefined ||
    speakerEmail !== undefined ||
    speakerBio !== undefined ||
    description !== undefined ||
    icon !== undefined ||
    order !== undefined;

  const mutation = useMutation({
    mutationFn: (body: PassportActivityUpdateInput) => updateActivity(activityId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['passport-admin', 'event', activity!.eventId],
      });
      void queryClient.invalidateQueries({ queryKey: ['passport-admin', 'activity', activityId] });
      toast.success('Actividad actualizada.');
      void navigate({ to: '/admin/passport/$eventId', params: { eventId: activity!.eventId } });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const sendQrMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/api/passport-admin/activities/${activityId}/send-qr-email`, {
        method: 'POST',
        schema: SendQrEmailResponse,
      }),
    onSuccess: (data) => {
      toast.success(`QR enviado a ${data.sentTo}`);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Error al enviar QR');
    },
  });

  const handleSubmit = () => {
    if (!effectiveTitle.trim()) {
      toast.error('El título es obligatorio.');
      return;
    }
    if ((effectiveType === 'TALK' || effectiveType === 'SPONSOR') && !effectiveSpeakerName.trim()) {
      toast.error('El ponente/empresa es obligatorio para este tipo.');
      return;
    }
    const body: PassportActivityUpdateInput = {
      type: effectiveType,
      title: effectiveTitle.trim(),
      speakerName:
        effectiveType === 'TALK' || effectiveType === 'SPONSOR'
          ? effectiveSpeakerName.trim()
          : null,
      speakerEmail: effectiveSpeakerEmail.trim() || null,
      speakerBio: effectiveSpeakerBio.trim() || null,
      description: effectiveDescription.trim() || null,
      icon: effectiveIcon ?? null,
    };
    if (effectiveOrder) body.order = Number(effectiveOrder);
    mutation.mutate(body);
  };

  if (isLoading) {
    return (
      <main className="flex-1 p-6 space-y-6">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-4" />
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-5 w-4" />
          <Skeleton className="h-5 w-28" />
        </div>
        <div className="space-y-4 max-w-lg">
          {Array.from({ length: 6 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      </main>
    );
  }

  if (isError || !activity) {
    return (
      <main className="flex-1 p-6 space-y-4">
        <nav className="flex items-center gap-1 text-sm text-muted-foreground">
          <Link to="/admin/passport" className="hover:text-foreground">
            Pasaporte
          </Link>
        </nav>
        <p className="text-destructive text-sm">No se pudo cargar la actividad.</p>
      </main>
    );
  }

  return (
    <main className="flex-1 p-6 space-y-6 max-w-lg">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm text-muted-foreground flex-wrap">
        <Link to="/admin/passport" className="hover:text-foreground">
          Pasaporte
        </Link>
        <span>/</span>
        <Link
          to="/admin/passport/$eventId"
          params={{ eventId: activity.eventId }}
          className="hover:text-foreground"
        >
          {activity.eventTitle}
        </Link>
        <span>/</span>
        <span className="text-foreground">Editar actividad</span>
      </nav>

      <h1 className="text-2xl font-semibold tracking-tight">Editar actividad</h1>

      <div className="space-y-4">
        {/* Tipo */}
        <div className="space-y-1.5">
          <Label>Tipo *</Label>
          <RadioGroup
            value={effectiveType}
            onValueChange={(v) => setType(v as PassportActivityType)}
            disabled={mutation.isPending}
            className="flex gap-4"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem id="type-talk" value="TALK" />
              <Label htmlFor="type-talk" className="font-normal cursor-pointer">
                Charla
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem id="type-secondary" value="SECONDARY" />
              <Label htmlFor="type-secondary" className="font-normal cursor-pointer">
                Actividad secundaria
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="SPONSOR" id="type-sponsor" />
              <Label htmlFor="type-sponsor" className="font-normal cursor-pointer">
                Patrocinador
              </Label>
            </div>
          </RadioGroup>
        </div>

        {/* Título */}
        <div className="space-y-1.5">
          <Label htmlFor="activity-title">Título *</Label>
          <Input
            id="activity-title"
            value={effectiveTitle}
            onChange={(e) => setTitle(e.target.value)}
            disabled={mutation.isPending}
            placeholder="Ej. Avances en cirugía"
          />
        </div>

        {/* Ponente / Empresa + Bio + Foto */}
        {(effectiveType === 'TALK' || effectiveType === 'SPONSOR') && (
          <>
            <div className="space-y-1.5">
              <Label htmlFor="activity-speaker">
                {effectiveType === 'SPONSOR' ? 'Empresa' : 'Ponente'} *
              </Label>
              <Input
                id="activity-speaker"
                value={effectiveSpeakerName}
                onChange={(e) => setSpeakerName(e.target.value)}
                disabled={mutation.isPending}
                placeholder="Ej. Dr. Juan Pérez"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="speaker-bio">
                {effectiveType === 'SPONSOR' ? 'Descripción de la empresa' : 'Bio del ponente'}
              </Label>
              <Textarea
                id="speaker-bio"
                value={effectiveSpeakerBio}
                onChange={(e) => setSpeakerBio(e.target.value)}
                disabled={mutation.isPending}
                placeholder={
                  effectiveType === 'SPONSOR' ? 'Empresa líder en...' : 'Especialista en...'
                }
                rows={3}
                maxLength={1000}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{effectiveType === 'SPONSOR' ? 'Logo' : 'Foto del ponente'}</Label>
              <SpeakerPhotoUpload
                activityId={activityId}
                currentPhotoUrl={activity.speakerPhotoUrl}
                onUploaded={() => {
                  void queryClient.invalidateQueries({
                    queryKey: ['passport-admin', 'activity', activityId],
                  });
                  void queryClient.invalidateQueries({
                    queryKey: ['passport-admin', 'event', activity.eventId],
                  });
                }}
              />
            </div>
          </>
        )}

        {/* Email del ponente */}
        <div className="space-y-1.5">
          <Label htmlFor="speaker-email">Email del ponente</Label>
          <Input
            id="speaker-email"
            type="email"
            value={effectiveSpeakerEmail}
            onChange={(e) => setSpeakerEmail(e.target.value)}
            disabled={mutation.isPending}
            placeholder="ponente@ejemplo.com"
          />
        </div>

        {/* Recursos */}
        <ResourcesList
          activityId={activityId}
          resources={activity.resources}
          eventId={activity.eventId}
        />

        {/* Descripción */}
        <div className="space-y-1.5">
          <Label htmlFor="activity-desc">Descripción</Label>
          <Textarea
            id="activity-desc"
            value={effectiveDescription}
            onChange={(e) => setDescription(e.target.value)}
            disabled={mutation.isPending}
            rows={2}
          />
        </div>

        {/* Ícono */}
        <div className="space-y-1.5">
          <Label>Ícono</Label>
          <div>
            <IconPicker value={effectiveIcon} onChange={setIcon} disabled={mutation.isPending} />
          </div>
        </div>

        {/* Orden */}
        <div className="space-y-1.5">
          <Label htmlFor="activity-order">Orden</Label>
          <Input
            id="activity-order"
            type="number"
            value={effectiveOrder}
            onChange={(e) => setOrder(e.target.value)}
            disabled={mutation.isPending}
            placeholder="Auto"
          />
        </div>

        <Button onClick={handleSubmit} disabled={mutation.isPending}>
          {mutation.isPending ? 'Guardando…' : 'Guardar cambios'}
        </Button>
      </div>

      {/* Código corto del ponente */}
      {activity.shortCode && activity.type === 'TALK' && (
        <div className="pt-4 border-t space-y-2">
          <p className="text-sm font-medium">Código de Ponente</p>
          <p className="text-xs text-muted-foreground">
            Léelo a los asistentes en tarima si no pueden escanear el QR. No distingue
            mayúsculas/minúsculas.
          </p>
          <div className="inline-block rounded-md border-2 border-dashed border-purple-600 bg-purple-50 px-5 py-3">
            <span className="font-mono text-3xl font-bold tracking-[0.4em] text-purple-700">
              {activity.shortCode}
            </span>
          </div>
        </div>
      )}

      {/* Enviar QR al ponente */}
      {effectiveSpeakerEmail && (
        <div className="pt-4 border-t space-y-2">
          <p className="text-sm font-medium">Enviar QR al ponente</p>
          <Button
            type="button"
            variant="outline"
            disabled={isDirty || sendQrMutation.isPending || !effectiveSpeakerEmail}
            title={isDirty ? 'Guarda los cambios antes de enviar' : undefined}
            onClick={() => setSendQrConfirmOpen(true)}
          >
            {sendQrMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Enviando…
              </>
            ) : (
              <>
                <Mail className="mr-2 h-4 w-4" />
                Enviar QR al ponente
              </>
            )}
          </Button>
          {isDirty && (
            <p className="text-xs text-muted-foreground">Guarda los cambios antes de enviar.</p>
          )}
        </div>
      )}

      <ConfirmDialog
        open={sendQrConfirmOpen}
        onOpenChange={setSendQrConfirmOpen}
        title="Enviar QR al ponente"
        description={`¿Enviar QR a ${effectiveSpeakerEmail}?`}
        confirmLabel="Enviar"
        variant="default"
        onConfirm={() => void sendQrMutation.mutate()}
      />
    </main>
  );
}
